import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Input,
  OnInit,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TUI_DARK_MODE } from '@taiga-ui/core/tokens';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { Observable } from 'rxjs';

import { BrandingService } from '@branding/branding.service';
import { SidePageService } from '@common/components/sidepage/sidepage.service';
import { AccessService } from '@common/services/access.service';
import { setStoredDarkTheme, syncLegacyDarkTheme } from '@common/utils/theme';
import { getCurrentUser } from '@store/actions/current_user.actions';
import { selectCurrentUser } from '@store/selectors/current_user.selectors';

import { environment } from '../../../../../environments/environment';
import { SettingsComponent } from '../../settings.component';
import { LocatorListComponent } from '../locator-list/locator-list.component';

@Component({
  selector: 'app-settings-tooltip',
  templateUrl: './settings-tooltip.component.html',
  styleUrls: ['./settings-tooltip.component.less'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SettingsTooltipComponent implements OnInit {
  protected readonly night = inject(TUI_DARK_MODE);
  private readonly sidepageService = inject<SidePageService>(SidePageService);
  private readonly injector = inject<Injector>(Injector);
  private readonly store = inject(Store);
  private readonly accessService = inject(AccessService);
  private readonly brandingService = inject(BrandingService);
  private readonly destroyRef = inject(DestroyRef);

  @Input() isDarkTheme = false;

  currentUser$: Observable<any> = this.store.select(selectCurrentUser);

  nightThemeControl = new FormControl<boolean>(false, { nonNullable: true });

  systemType = environment.systemType;

  isVisibleDocumentationLink = this.brandingService.brand.isCarteBlanche;

  get accessLocators() {
    return this.accessService.checkUserCommonAccess(['access_locators']);
  }

  constructor() {
    syncLegacyDarkTheme(this.night);
  }

  ngOnInit(): void {
    if (environment.systemType === 'cms') {
      this.store.dispatch(getCurrentUser());
    }

    if (this.isDarkTheme) {
      this.nightThemeControl.setValue(this.night(), { emitEvent: false });
      this.nightThemeControl.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((value) => {
          this.night.set(value);
          setStoredDarkTheme(value);
        });
    }
  }

  exit() {
    sessionStorage.clear();
    window.location.href = '/';
  }

  openAccountSettings(user: any = null) {
    this.sidepageService
      .open<boolean>(new PolymorpheusComponent(SettingsComponent, this.injector), {
        size: 'l',
        headingTranslate: 'Settings',
        data: { user },
      } as any)
      .subscribe();
  }

  openLocatorPopup() {
    this.sidepageService
      .open<boolean>(new PolymorpheusComponent(LocatorListComponent, this.injector), {
        size: 'l',
        headingTranslate: 'Locator',
        data: {},
      } as any)
      .subscribe();
  }
}
